import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useRequestActions, useRequestOptions } from '../../hooks';
import { RequestSchema, defaultValues } from '../../models';
import {
  FormDateInput,
  FormSelectInput,
  FormSubmitButton,
  FormTextAreaInput,
  FormTextInput,
  NavigateLinks,
  GlobalButton,
} from '@/components';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { SearchUser } from '@/pages/Inventory/components';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import { requestsPageBreadcrumbs } from '../../config';
import { useGetRequestByIdQuery } from '../../services';
import { useUsersOptions } from '@/pages/Inventory/hooks';

const EditRequest = () => {
  const { id = '' } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { onUpdateRequest, isUpdatingRequest, onDeleteRequest } =
    useRequestActions();

  const { data: requestToEdit, isLoading: isLoadingRequest } =
    useGetRequestByIdQuery({ requestId: id }, { skip: !id });

  const { control, handleSubmit, reset, setValue, formState, watch } =
    useForm<RequestSchema>({
      resolver: zodResolver(RequestSchema),
      defaultValues,
      mode: 'onChange',
      shouldFocusError: false,
    });

  const { requestTypeOptions } = useRequestOptions();
  const { userOptions } = useUsersOptions({});
  const breadcrumbs = useMemo(() => requestsPageBreadcrumbs(navigate), []);

  const selectedType = watch('type');
  const userIdValue = watch('userId');
  
  const selectedUserOption = userOptions.find((opt: any) => opt.data.id === userIdValue) || null;

  useEffect(() => {
    if (requestToEdit) {
      reset({
        adminId: requestToEdit.adminId,
        userId: requestToEdit.userId,
        type: requestToEdit.type,
        otherType: requestToEdit.otherType || '',
        note: requestToEdit.note || '',
        neededAt: requestToEdit.neededAt
          ? new Date(requestToEdit.neededAt)
          : undefined,
        status: requestToEdit.status,
        handledBy: requestToEdit.handledBy || '',
        responseNote: requestToEdit.responseNote || '',
      });
    }
  }, [requestToEdit, reset]);

  if (isLoadingRequest) {
    return (
      <section className='flex flex-col w-full h-full gap-10'>
        <NavigateLinks actualPage={id} breadcrumbs={breadcrumbs} />
        <div className='flex justify-center items-center h-64'>
          <div className='text-lg'>Cargando solicitud...</div>
        </div>
      </section>
    );
  }

  if (!requestToEdit) {
    return (
      <section className='flex flex-col w-full h-full gap-10'>
        <NavigateLinks actualPage={id} breadcrumbs={breadcrumbs} />
        <div className='flex justify-center items-center h-64'>
          <div className='text-lg text-red-600'>Solicitud no encontrada</div>
        </div>
      </section>
    );
  }

  return (
    <section className='flex flex-col w-full h-full gap-5'>
      <NavigateLinks actualPage={id} breadcrumbs={breadcrumbs} />
      <div className='w-full max-w-2xl mx-auto flex justify-end'>
        <GlobalButton
          variant={GlobalButtonVariant.secondaryButton}
          action={() => {
            if (
              window.confirm(
                '¿Estás seguro de que deseas eliminar esta solicitud?'
              )
            ) {
              onDeleteRequest(id, () => navigate('/inventory/requests'));
            }
          }}
        >
          <DeleteIcon className='text-red-600' />
          <span>Eliminar solicitud</span>
        </GlobalButton>
      </div>
      <form className='flex flex-col gap-6 bg-white p-12 rounded-lg shadow-md mx-auto max-w-2xl w-full'>
        <h1 className='text-3xl font-black select-none'>Editar solicitud</h1>
        <div className='flex gap-4'>
        <SearchUser
          name='userId'
          placeholder='Usuario'
          control={control}
          required
          onUserSelect={(userId) => {
            setValue('userId', userId, {
              shouldValidate: true,
              shouldDirty: true,
            });
          }}
          showSelectedUserInfo={true}
          value={selectedUserOption}
        />
        
        <FormDateInput
          name='neededAt'
          placeholder='Fecha necesaria'
          control={control}
        />
        </div>
        <FormSelectInput
          name='type'
          placeholder='Seleccionar tipo de equipo'
          control={control}
          options={requestTypeOptions}
          required
        />

        {selectedType === 'OTHER' && (
          <FormTextInput
            name='otherType'
            placeholder='Especificar tipo de equipo'
            control={control}
            required
          />
        )}

        <FormTextAreaInput
          name='note'
          placeholder='Motivo de la solicitud'
          control={control}
          rows={3}
        />

        <div className='flex justify-end gap-4 mt-2'>
          <FormSubmitButton
            icon={<SaveIcon />}
            action={handleSubmit((formData) => {
              const payload = {
                ...formData,
                neededAt:
                  formData.neededAt == null ? null : formData.neededAt,
              };
              onUpdateRequest(id, payload as any, () => navigate('/inventory/requests'));
            })}
            disabled={!formState.isDirty || !formState.isValid}
            isLoading={isUpdatingRequest}
            label='Actualizar solicitud'
          />
        </div>
      </form>
    </section>
  );
};

export default EditRequest;
